import { useCallback, useEffect, useState } from "react";
import { AlarmClock, Bell, BellOff, CalendarDays, ExternalLink, MapPin, Pencil, Plus, LogIn } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReminderBanner from "@/components/ReminderBanner";

const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const target = new Date(isoDate + "T00:00:00Z");
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
};

const deadlineLabel = (days) => {
  if (days === null || days === undefined) return null;
  if (days < 0) return "Meldeschluss abgelaufen";
  if (days === 0) return "Meldeschluss HEUTE";
  if (days === 1) return "Meldeschluss MORGEN";
  return `Meldeschluss in ${days} Tagen`;
};

function CalendarImage({ event }) {
  // Bild kommt über den Cache-freundlichen Endpoint statt inline Base64 →
  // Übersicht bleibt klein, Bild lädt lazy und wird vom Browser 1 h gepuffert.
  const [ok, setOk] = useState(true);
  if (!ok) return <div className="calendar-image-fallback"><CalendarDays size={42} /></div>;
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/events/${event.id}/image`;
  return (
    <div className="calendar-image-wrap" data-testid={`calendar-image-wrap-${event.id}`}>
      <img src={src} alt={event.name} loading="lazy" onError={() => setOk(false)} data-testid={`calendar-image-${event.id}`} />
    </div>
  );
}

export default function EventCalendarPage() {
  const [events, setEvents] = useState([]);
  const [followedIds, setFollowedIds] = useState(new Set());
  const [busyId, setBusyId] = useState(null);
  const [reminderRefresh, setReminderRefresh] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === "super_admin";

  const loadFollowed = useCallback(async () => {
    try {
      const response = await api.get("/me/followed-events");
      setFollowedIds(new Set(response.data.map((event) => event.id)));
    } catch {
      setFollowedIds(new Set());
    }
  }, []);

  useEffect(() => {
    api.get("/calendar").then((response) => setEvents(response.data)).catch(() => setEvents([]));
    loadFollowed();
  }, [loadFollowed]);

  const toggleFollow = async (eventId) => {
    setBusyId(eventId);
    try {
      if (followedIds.has(eventId)) {
        await api.delete(`/events/${eventId}/follow`);
      } else {
        await api.post(`/events/${eventId}/follow`);
      }
      await loadFollowed();
      setReminderRefresh((current) => current + 1);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="content-frame" data-testid="event-calendar-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">WBC GERMANY</p>
          <h1>EVENTKALENDER</h1>
          <p className="muted">Kommende Meisterschaften, Ausschreibungen und Kämpfermeldungen – chronologisch nach Datum.</p>
        </div>
        {isSuperAdmin && (
          <Link to="/calendar/admin" className="button primary" data-testid="calendar-edit-button">
            <Pencil size={18} /> KALENDER PFLEGEN
          </Link>
        )}
      </header>
      <ReminderBanner refreshKey={reminderRefresh} />
      <section className="event-feed">
        {events.map((event) => {
          const deadlineDays = daysUntil(event.registration_deadline);
          const isFollowed = followedIds.has(event.id);
          const isUrgent = deadlineDays !== null && deadlineDays >= 0 && deadlineDays <= 7;
          const isRegistrationClosed = deadlineDays !== null && deadlineDays < 0;
          return (
            <article className={`calendar-post ${isUrgent ? "urgent" : ""}`} key={event.id} data-testid={`calendar-event-${event.id}`}>
              <CalendarImage event={event} />
              {isUrgent && <span className="calendar-urgent-badge" data-testid={`calendar-urgent-${event.id}`}><AlarmClock size={13} /> DRINGEND</span>}
              <div className="calendar-post-body">
                <p>{event.date}</p>
                <h2>{event.name}</h2>
                <span><MapPin size={15} /> {event.location}</span>
                {event.registration_deadline && (
                  <span className={`calendar-deadline ${isUrgent ? "urgent" : ""}`} data-testid={`calendar-deadline-${event.id}`}>
                    <AlarmClock size={15} /> {deadlineLabel(deadlineDays)}
                  </span>
                )}
                {event.description && <p className="calendar-description">{event.description}</p>}
                <div className="calendar-post-actions">
                  {isRegistrationClosed ? (
                    <span className="button ghost" data-testid={`calendar-registration-closed-${event.id}`} style={{background:"#2a1a1a",color:"#a89a70",cursor:"default"}}>MELDUNG GESCHLOSSEN</span>
                  ) : (
                    <button type="button" className="button primary calendar-cta" data-testid={`calendar-register-fighter-${event.id}`} onClick={() => user ? navigate(`/events/${event.id}?action=register`) : navigate(`/login?next=/events/${event.id}?action=register`)}>
                      {user ? <><Plus size={16} /> KÄMPFER MELDEN</> : <><LogIn size={16} /> KÄMPFER MELDEN</>}
                    </button>
                  )}
                  {user && (
                    <button
                      type="button"
                      className={`button ${isFollowed ? "ghost" : "ghost"} calendar-follow-button`}
                      onClick={() => toggleFollow(event.id)}
                      disabled={busyId === event.id}
                      data-testid={`calendar-follow-${event.id}`}
                    >
                      {isFollowed ? <><BellOff size={16} /> ERINNERUNG AUS</> : <><Bell size={16} /> ERINNERUNG</>}
                    </button>
                  )}
                  {event.registration_url && (
                    <a href={event.registration_url} target="_blank" rel="noreferrer" className="button ghost" data-testid={`calendar-registration-link-${event.id}`}>
                      Externer Meldelink <ExternalLink size={16} />
                    </a>
                  )}
                </div>
              </div>
            </article>
          );
        })}
        {!events.length && (
          <div className="empty-state" data-testid="calendar-empty-state">
            <CalendarDays size={32} />
            <h2>NOCH KEINE KOMMENDEN EVENTS</h2>
            <p>Sobald neue Turniere angekündigt werden, erscheinen sie hier automatisch.</p>
          </div>
        )}
      </section>
    </div>
  );
}
